import { ICourse2 } from "@et/types/Courses";
import { PAYMENT_TYPES_THEME } from "@et/types/Enums";
import { neonPink } from "@styles/themes/darkThemeBeautifulLettering";
import { graphql } from "gatsby";
const slug = 'stationery-design-in-procreate'
const title = 'Stationery Design in Procreate'
const highlight1 = `#B77DC3`
const mainColor = `#4D286F`
const course: ICourse2 = {
  theme: PAYMENT_TYPES_THEME.LIGHT,
  title,
  slug,
  videoId: `nfb07pgvxs`,
  buyNowPayLater: null,
  keywords: [
    'stationery design', 'Procreate stationery', 'custom stationery', 'Procreate journals', 'Procreate stickers', 'digital art to physical products', 'Procreate greeting cards', 'Procreate postcards', 'print at home stationery', 'online printer for Procreate', 'Procreate notepads', 'Procreate notebooks', 'Procreate pouches', 'Procreate bookmarks', 'DIY stickers Procreate', 'Cricut sticker designs', 'Procreate print templates', 'home printing Procreate', 'Procreate color profiles', 'Procreate composition', 'Procreate design skills', 'Procreate artwork projects', 'watercolor in Procreate', 'Procreate floral designs', 'Procreate for iPad', 'Procreate class', 'Procreate courses', 'Procreate tutorials', 'Procreate pattern design', 'Procreate surface design', 'Procreate text hierarchy', 'Procreate typography', 'Procreate color palettes', 'Procreate passive income', 'Procreate monetization'
  ],
  price: '297',
  salePrice: '247',
  paymentPlan: {
    price: '80',
    perMonth: '4 Payments of $80/m',
    savings: '10%',
    salePrice: '85',
  },
  motionMail: `https://s.mmgo.io/t/Cxvn/scale_2x`,
  onSale: false,
  tagline: 'Create beautiful stationery designs in Procreate!',
  classDescriptor: 'Create beautiful stationery designs in Procreate!',
  intro: {
    imgName: 'procreate301/tablet-intro.png',
    imgAlt: 'Create custom modern lettering styles that are unique to you',
    bullets: [
      {
        title: 'Beginner Friendly',
        desc:
          'Step-by-step narration with closeups on all the details, so you feel confident in the process from start to finish.',
      },
      {
        title: 'A Versatile Process',
        desc:
          'Since every lettering style is different, this process has been fine-tuned to fit any situation. You’ll be able to achieve a realistic watercolor effect when you apply it to any style - hand lettered or typed.',
      },
      {
        title: 'Blend Options',
        desc:
          'You may want an ombré, one color, smooth transitions blend with one of your pieces and a multi-colored blend with water pockets on another. This course serves up 5 different blend options, so you’ll have plenty to choose from when you begin your next lettering piece.',
      },
      {
        title: 'Florals + Foliage Integration',
        desc:
          'Take your watercolor lettering even further by integrating florals and foliage! A bonus florals foundations module is included, so even if you’ve never painted florals before, you’ll be able to follow along and complete every project in the class.',
      },
    ],
  },
  projects: {
    beginner: [
      {
        title: 'Project 1',
        backgroundColor: `#c4d8da`,
        textColor: `#4a5457`,
        subheadColor: `#fff`,
        projectImg: `procreate301/project-1.jpg`,
        projectImgMobile: `procreate301/project-1.jpg`,
        type: '',
        desc:
          'We kick things off with a seashell symmetry full drop pattern. We\'ll set up our template together using the symmetry block brushes, paint in the elements, then make all edges seamless through pre-programmed selections. Once we have all of our gaps filled in, we\'ll drag it into the Pattern Playground for a final test.',
      },
      {
        title: 'Project 2',
        backgroundColor: `#fcbfb2`,
        textColor: `#4a5457`,
        subheadColor: `#fcb900`,
        projectImg: `procreate301/project-2.jpg`,
        projectImgMobile: `procreate301/project-2-mobile.jpg`,
        type: '',
        desc:
          'To reinforce what we covered in Project 1, we\'ll use another symmetry block brush - this time the flower shape, and create another full drop pattern. I share how I fix troublesome areas when elements moving in different directions collide and I explain my full thought process along with my reasons behind every decision I make. We reconfigure our pattern to make it seamless using our pre-programmed selections and do a final test using the Pattern Playground.',
      },
      {
        title: 'Project 3',
        backgroundColor: `#ffe3df`,
        textColor: `#4a5457`,
        subheadColor: `#f3ecdf`,
        projectImg: `procreate301/project-3.jpg`,
        projectImgMobile: `procreate301/project-3-mobile.jpg`,
        type: '',
        desc: 'In Project 3, we create a full drop repeat using an included composition template. Following the composition template, we\'re able to speed up our workflow and quickly adjust and adapt it to fit our needs. Once our elements are in place, we use our pre-programmed selections within the template to fill in the gaps, then test + adjust as needed using the Pattern Playground.',
      },
      {
        title: 'Project 4',
        projectImg: `procreate501/project-4.jpg`,
        projectImgMobile: `procreate301/project-4-mobile.jpg`,
        backgroundColor: `#dee5dd`,
        textColor: `#565857`,
        subheadColor: `#c56b00`,
        type: '',
        desc:
          'It\'s time to head into half drop patterns in Project 4! We\'ll work with a different composition template this time, pre-programmed with selections and set up specifically for half drops. After painting our elements in, we\'ll reorganize the template to fill in gaps using our selections, then test + adjust using the Pattern Playground.',
      },
      {
        title: 'Project 5',
        projectImg: `procreate301/project-5.jpg`,
        projectImgMobile: `procreate301/project-5-mobile.jpg`,
        backgroundColor: `#ffd5bc`,
        textColor: `#4a5457`,
        subheadColor: `#419187`,
        type: '',
        desc:
          'To round out our pattern types, Project 5 is all about the half brick. We\'ll pick a half brick specific composition template, lay in our artwork and go off script from the template as needed. We then reorganize our artwork using the pre-programmed selections to fill in gaps and test our completed half brick within the Pattern Playground.',
      },
    ],
    advanced: [],
  },
  curriculum: [
    {
      title: 'Welcome',
      videos: [
        {
          title: 'Course Overview: What You\'ll Learn',
          duration: '7:57',
        },
        {
          title: 'Class Bonuses Location/Install Instructions',
          duration: '8:39',
        },
        {
          title: 'Procreate Gestures used in the Course',
          duration: '6:04',
        },
        {
          title: ' iPad Specs + Accessories',
          duration: '5:52',
        }
      ],
    },
    {
      title: 'Composition Anatomy',
      videos: [
        {
          title: 'Dissecting Portrait Layouts: Part 1',
          duration: '4:53',
        },
        {
          title: 'Dissecting Portrait Layouts: Part 2',
          duration: '8:03',
        },
        {
          title: 'Dissecting Portrait Layouts: Part 3',
          duration: '4:15',
        },
        {
          title: 'Dissecting Landscape Layouts: Part 1',
          duration: '4:10',
        },
        {
          title: 'Dissecting Landscape Layouts: Part 2',
          duration: '4:45',
        },
        {
          title: ' Dissecting Square Layouts',
          duration: '5:32',
        },
        {
          title: 'Dissecting Headline Only Layouts: Part 1',
          duration: '4:18',
        },
        {
          title: 'Dissecting Headline Only Layouts: Part 2',
          duration: '5:53',
        },
        {
          title: 'Dissecting Headline Only Layouts: Part 3',
          duration: '5:28',
        },
        {
          title: 'Dissecting Stationery Collections',
          duration: '6:37',
        },

      ]
    },
    {
      title: 'How to Use the Class Bonuses',
      videos: [
        {
          title: 'How to Use the Canvas Templates',
          duration: '5:19',
        },
        {
          title: 'How to Use the Composition Templates',
          duration: '4:27',
        },
        {
          title: 'Artist Essentials Overview',
          duration: '4:51',
        },
        {
          title: 'Holding Shape Brushes Overview',
          duration: '2:35',
        },
      ],
    },
    {
      title: 'Creating Your Visual Style System (VSS)',
      videos: [
        {
          title: 'What is a VSS?',
          duration: '3:57',
        },
        {
          title: 'Type Pairings/Lockups',
          duration: '7:08',
        },
        {
          title: 'Choosing a Color Palette',
          duration: '6:54',
        },
        {
          title: 'Choosing + Creating your Elements',
          duration: '11:59',
        },
        {
          title: 'Create your VSS Board',
          duration: '7:48',
        },
      ],
    },
    {
      title: 'Printing Considerations',
      videos: [
        {
          title: 'RGB vs. CMYK // Home Printing vs. Online Printing',
          duration: '3:35',
        },
        {
          title: 'Paper Considerations',
          duration: '3:24',
        },
      ],
    },
    {
      title: 'Project 1: Print at Home Greeting Card',
      videos: [
        {
          title: 'Choose a Canvas + Composition',
          duration: '2:30',
        },
        {
          title: 'Create the Design + Prep Test Prints',
          duration: '9:28',
        },
        {
          title: 'Test Printing',
          duration: '3:59',
        },
        {
          title: 'Final Print + Cut, plus Embellishment Options',
          duration: '3:27',
        },
        {
          title: 'Quick Recoloring Option Demo',
          duration: '4:00',
        },
      ]
    },
    {
      title: 'Project 2: Print at Home Invitation Postcard',
      videos: [
        {
          title: 'Choose a Canvas + Composition',
          duration: '1:30',
        },
        {
          title: 'Determine Text Hierarchy/Sketch Layer',
          duration: '2:59',
        },
        {
          title: 'Setting the Text',
          duration: '6:11',
        },
        {
          title: 'Add Elements, Text Color + Prep for Print',
          duration: '6:27',
        },
        {
          title: 'Final Print + Cut',
          duration: '1:25',
        },
      ]
    },
    {
      title: 'Project 3: Notepad (Online Printer)',
      videos: [
        {
          title: 'A Quick Word About Online Printers',
          duration: '1:21',
        },
        {
          title: 'Choose Online Printer + Download Template',
          duration: '2:46',
        },
        {
          title: 'Upload + Purchase',
          duration: '8:58',
        },
        {
          title: 'Printed Product',
          duration: '4:17',
        }
      ]
    },
    {
      title: 'Project 4: Notebook (Online Printer)',
      videos: [
        {
          title: 'Choose Online Printer + Download Template',
          duration: '5:48',
        },
        {
          title: 'Design, Upload + Purchase the Notebook',
          duration: '7:31',
        },
        {
          title: 'Printed Product',
          duration: '4:09',
        }
      ]
    },
    {
      title: 'Project 5: Accessory Pouch (Online Printer)',
      videos: [
        {
          title: 'Choose Product, Download + Adjust Template',
          duration: '4:39',
        },
        {
          title: 'Design Overview + Upload and Purchase',
          duration: '4:09',
        },
        {
          title: 'Printed Product',
          duration: '4:52',
        }
      ]
    },
    {
      title: 'Bonus 1: Print at Home Bookmarks',
      videos: [
        {
          title: 'Choose a Canvas + Paint First Bookmark',
          duration: '5:07',
        },
        {
          title: ' Finish Bookmarks in Different Colorways',
          duration: '7:15',
        },
        {
          title: 'Print, Cut, Laminate + Results!',
          duration: '4:43',
        },

      ]
    },
    {
      title: 'Bonus 2: Individual Stickers',
      videos: [
        {
          title: 'Individual Stickers with an Online Service',
          duration: '8:08',
        },
        {
          title: '[DIY] Create your Stickers + Print Prep',
          duration: '5:24',
        },
        {
          title: 'Print Settings',
          duration: '7:27',
        },
        {
          title: 'Laminate + Prep for Cutting',
          duration: '4:16',
        },
        {
          title: 'Cut Settings',
          duration: '1:12',
        },
        {
          title: 'Results',
          duration: '3:31',
        },
      ]
    },
    {
      title: 'Bonus 3: Sticker Sheets using a Cricut',
      videos: [
        {
          title: 'Project Overview',
          duration: '2:07',
        },
        {
          title: 'Create your Sticker Sheets + Print Prep',
          duration: '6:37',
        },
        {
          title: 'Upload the Files + Print',
          duration: '4:33',
        },
        {
          title: 'Laminate the Sheets',
          duration: '2:54',
        },
        {
          title: 'Cut Settings',
          duration: '2:45',
        },
        {
          title: 'Cut the Sheets + Results!',
          duration: '1:59',
        },
      ]
    },
    {
      title: 'Bonus 4: Monetizing your Collection [Passive + Active Options]',
      videos: [
        {
          title: 'Monetizing Stationery: Passive + Active Overview',
          duration: '2:36',
        },
        {
          title: 'Clip Art Sets: Research, Examples + Price Points',
          duration: '9:40',
        },
        {
          title: 'Clip Art Sets: Creating + Exporting Your Designs',
          duration: '4:58',
        },
        {
          title: 'Clip Art Sets: How to Increase Sales + Price Points, Where to Sell',
          duration: '11:59',
        },
        {
          title: 'Invitation Templates: Research, Examples + Price Points',
          duration: '7:30',
        },
        {
          title: 'Invitation Templates: Convert your Procreate Templates to Photoshop Templates',
          duration: '7:53',
        },
        {
          title: 'Invitation Templates: Convert your Procreate Templates to Sellable Canva Templates (part 1)',
          duration: '6:41',
        },
        {
          title: 'Invitation Templates: Create the Sellable Canva File (part 2)',
          duration: '4:07',
        },
        {
          title: 'POD Stationery: The Basics',
          duration: '6:14',
        },
        {
          title: 'Customizable Templates: Overview, Listing + Selling',
          duration: '2:19',
        },
        {
          title: 'Custom Stationery Design: Examples, Listing + Selling',
          duration: '2:12',
        },
        {
          title: 'Selling + Shipping Stationery Products: Considerations & Tips',
          duration: '4:12',
        }
      ]
    },
    {
      title: 'Next steps',
      videos: [
        {
          title: 'Ideas for the Future + Thank You',
          duration: '2:59',
        },
      ],
    },
  ],
  bonuses: {
    headerImgName: `procreate301/bonus-title.png`,
    imgAlt: 'Messy Watercolors in Procreate Bonuses',
    gridType: 1,
    items: [
      {
        title: 'Artist Essentials Brush Set + Extended License',
        desc: '<p>The full 16 piece drawing brush set is included with enrollment, so you’ll have access to premium quality painting, sketching and drawing brushes in a variety of media types. An extended license is included, so you can use the brushes to create artwork you plan to sell without limit.</p>',
        imgName: 'stationery-design-in-procreate/bonuses/bonus-1.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Holding Shapes Brush Set + Extended License',
        desc: '<p>Holding shapes break up dense areas of illustration, offer a placeholder for text and can add visual interest when combating excessive whitespace. No need to deal with the symmetry tool or line any edges up exactly! Simply stamp in the desired shape (there’s 26 of them!) and you’re ready to go. Extended license included, so you can use these within any future designs you plan to sell without limit.</p>',
        imgName: 'stationery-design-in-procreate/bonuses/bonus-2.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Composition Template Stamp Brushes',
        desc: '<p>There’s no longer any need to stress about creating balanced compositions! Simply choose a composition stamp and stamp it in to get started! Place your elements where you see scribbles and text where you see rectangles. All rectangles are sized for hierarchy, so there’s no guesswork there, either! And with 32 stamps, there’s a composition available for any application!</p>',
        imgName: 'stationery-design-in-procreate/bonuses/bonus-3.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: "Procreate Canvases For Printables",
        desc: '<p>If you love printing stationery at home, but struggle to size your greeting cards, postcards, gift tags or bookmarks in Procreate, this set of 17 canvas templates is for you! All canvas templates work with any 8.5” x 11” or A4 sized paper with bleed lines, cut and score lines all defined. Additional tutorials throughout the class show how to use the canvases extensively, so your print at home stationery just got a *major* time saving + process upgrade! Extended License also included, so you can sell your at home printables using the templates!</p>',
        imgName: 'stationery-design-in-procreate/bonuses/bonus-4.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Style Board Template',
        desc: '<p>Part of creating a cohesive stationery collection is implementing a visual style system. We walk through the process of creating a system together and use a style board to define our final work. Use and reuse this style board for all future collections and pieces to maintain consistency throughout all of your stationery work!</p>',
        imgName: 'stationery-design-in-procreate/bonuses/bonus-5.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Type Pairing + Lockups Cheat Sheet',
        desc: '<p>If you’ve ever struggled to choose and pair fonts for your artwork, this cheat sheet will provide 5 golden rules for creating pro-style pairings and type lockups – examples included!</p>',
        imgName: 'stationery-design-in-procreate/bonuses/bonus-6.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Online Print Vendors List + Other Resources',
        desc: '<p>An extensive list of recommended sites, products and vendors from my career designing and selling stationery artwork (vendors I’ve *actually* used and have experience with – not just a list of googled recs). The list is categorized and includes additional notes on suggestions and tips when using specific ones.</p>',
        imgName: 'stationery-design-in-procreate/bonuses/bonus-7.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Private Facebook Community',
        desc: '<p>A kind, supportive and wonderful group of other Procreate artists to design beautiful stationery alongside. The perfect place to find answers to questions, get feedback on your work and a non stop source of support from the best Procreate makers around. I’m always in there, too!</p>',
        imgName: 'stationery-design-in-procreate/bonuses/bonus-8.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Print at Home Bookmarks',
        desc: '<p>In our first bonus project, you’ll learn how to apply 2 additional colorways to your artwork, following your design system’s color palette. We’ll embellish, then laminate our bookmarks (without a laminator!) and walk away with a gorgeous, print at home product that will last for years.</p>',
        imgName: 'stationery-design-in-procreate/bonuses/bonus-9.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Stickers!',
        desc: '<p>Our second and third bonus projects focus on custom sticker making! You’ll learn how to use an online printer to produce your stickers, and I also share the more cost-effective approach of using a Cricut Maker for all of them! Learn how to set up your own custom die cut layers, print individual stickers onto laminated sticker paper as well as set up your own sticker sheets that will maintain the sticker’s backing once peeled. These are sell-worthy quality as we print on both white vinyl *and* transparent inkjet sticker paper, making the possibilities endless!</p>',
        imgName: 'stationery-design-in-procreate/bonuses/bonus-10.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: '3 Passive Income Monetization Opportunities',
        desc: '<p>In our bonus monetization module, you’ll learn how to create 3 different passive income streams from your new stationery skills, complete with tutorials on how to accomplish each one. Passive income tutorials include creating clipart from your system elements, converting your Procreate templates into sellable PSD files *and* Canva templates (for free!) and setting up a POD (print on demand) product shop.</p>',
        imgName: 'stationery-design-in-procreate/bonuses/bonus-11.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: '3 Active Income Monetization Opportunities',
        desc: '<p>Our bonus monetization module also includes 3 different active income stream ideas! Those videos include setting up your customizable templates as a service for buyers, creating custom stationery designs for clients (with examples, tips for listing and selling) as well as considerations and tips for selling and shipping unique stationery products directly to your buyers (inspiration examples included!).</p>',
        imgName: 'stationery-design-in-procreate/bonuses/bonus-12.jpg',
        bgColor: '#FFF6EE'
      },
    ],
  },
  courseCard: {
    title: 'Messy Watercolors in Procreate',
    backgroundImgName: ``,
    backgroundColor: '#65807c',
    cardImgName: `procreate301/watercolor-class-card-4.jpg`,
    desc: 'Paint vibrant and messy eye candy watercolor artwork in Procreate! Reference traditional methods while utilizing digital advantages like blend modes, selective color adjustments and layers. Follow along as each project builds in complexity with technique, process and levels of messiness. In addition to painting, you’ll learn and use multiple layers, blend modes, selections, recoloring, saturation adjustments and warping to create stunning artwork using an efficient process. Finish the course by learning how to prepare your artwork for print using a home printer or online print services to enjoy your art beyond the screen.',
    bullets: [
      'Ink Lovers Brush Set',
      'Symmetry Block Stamp Brushes',
      'All Practice and Project Color Palettes',
      'Pattern Types + Styles Overview',
      '4 Easy Florals + Foliage Drawing Tutorials',
      '2 Methods for Full Drop Repeats',
      'Pattern Playground (pattern tester + previewer)',
      '2 Symmetry Block Pattern Projects',
      '15 Pattern Composition Canvas Templates',
      '3 Blank Pattern Templates with Pre-Programmed Selections',
      'Tutorials for Adapting Templates to Any Canvas Size',
      'Full Drop Pattern Project using a Composition Template',
      'Half Drop Pattern Project using a Composition Template',
      '2 Half Brick Pattern Projects using Composition Templates',
      'Develop Multiple Pattern Colorways Tutorial',
      '3 Procreate Pattern Mockup Tutorials',
      'Exporting your Patterns + Getting them Printed Module',
      'Spoonflower Fabric + Wallpaper Video Buyer\'s Guide',
      'Wallpaper Application Timelapse with Narration',
      'How to Convert your Procreate Patterns into Rasterized Illustrator Swatches',
      'How to Convert your Procreate Patterns into Photoshop .PAT Files',
      'Pattern Types Cheat Sheet',
      'Easy Florals + Foliage Cheat Sheet',
      'Helpful Links Section with Pattern Making Online Resources',
      'Membership in Our Private Facebook Community',
    ],
    brush: {
      enabled: true,
      image: {
        pencil: `apple2-pencil.png`,
        shadow: `sell-card-pencil-reflection-light.png`,
      },
    },
    priceColor: mainColor,
    saleColor: neonPink,
    stats: {
      modules: `#cc836f`,
      videos: `#758384`,
      hours: `#a79e8d`,
    },
  },
  faqs: [
    {
      question: 'What if I’ve never designed stationery before or have limited drawing skills?',
      answer: '<p>Then I’m excited for you to experience it for the first time in Procreate! This class is very beginner friendly and perfect for the stationery lover that has always wanted to design but didn’t know where to start or the crafter who wants to expand their knowledge or skills. Even simple doodles can be expanded into gorgeous, pro-level stationery systems, so no matter your drawing skills, you’ll be able to follow along with each project.</p>',
    },
    {
      question: 'I’m brand new to Procreate, will I be able to follow along?',
      answer: '<p>This course was created with beginners in mind, though if you’re BRAND new to Procreate, check out my <a target="_blank" href="https://learn.every-tuesday.com/procreate-for-beginners">free course, Procreate for Beginners</a> first and you’ll cruise through more comfortably.</p>',
    },
    {
      question: 'What do I need to take this course?',
      answer: '<p>You’ll need an iPad that supports pressure sensitivity along with a compatible stylus (<a target="_blank" href="https://support.apple.com/en-us/HT211029">check out this list</a>) and the Procreate app (v.5.3.7 or newer) installed. Two bonus projects utilize a Cricut Maker which is optional (an option for using an online printer instead is provided) and we also use the free version of Canva in one of the monetization tutorials, which is also optional, making the course fully accessible with just an iPad!</p>',
    },
    {
      question: 'I’m not sure I have time for this?',
      answer: '<p>All Every Tuesday courses come with lifetime access, so you can take it at the time and pace that works best for you and your schedule. It’s yours for the long haul.</p>',
    },
    {
      question: 'Do you offer any refunds?',
      answer: '<p>This course is backed by our 14 day money back guarantee. If you take the course and feel your Procreate stationery skills and/or knowledge have not benefited from the course, send us an email before the 14 days is up for a full refund.</p>',
    },
    {
      question: 'Do I have to have Photoshop to take this course?',
      answer: '<p>Nope! I share how to convert your Procreate templates into sellable Photoshop templates in the monetization module, but it’s all done completely in Procreate, so access to Photoshop is not necessary.</p>',
    },
    {
      question: 'Does this class come with subtitles/closed captions?',
      answer: '<p>Yes! English subtitles are provided for every video.</p>'
    },
    {
      question: 'How long do I get access?',
      answer: '<p>You buy something, you get to keep it forever around here.</p>',
    },
    {
      question: 'Can I sell the class projects I recreate as my own?',
      answer: '<p>Since the style system I create and share in the class is my own unique concept/artwork, you may not duplicate it and sell it as your own, but you’re welcome to use the methods, processes and brushes to create your own unique concept and sell the same projects based on your own artwork (and I hope you do!).</p>',
    },
    {
      question: 'I have more questions, how can I get in touch?',
      answer: '<p>Head to <a target="_blank" href="https://every-tuesday.com/contact">my contact page</a> and send em over! I’m just an email away.</p>',
    },
  ],
  testimonials: [],
  letsDoThis: {
    imgName: 'procreate301/lets-do-this-2.jpg',
    altTag: 'Enroll in Messy Watercolors for Procreate today!',
    text: 'Welcome to Flat Florals in Procreate, your complete guide to painting simplified, bold and beautiful florals.',
  },
  sellCard: {
    priceColor: '#5f1212',
    saleColor: neonPink,
    cardImg: '',
    saleLink: 'https://courses.every-tuesday.com/purchase?product_id=5583686',
    link: 'https://courses.every-tuesday.com/purchase?product_id=5478516',
    paymentPlanLink: 'https://courses.every-tuesday.com/purchase?product_id=5583675',
    paymentPlanSaleLink: 'https://courses.every-tuesday.com/purchase?product_id=5583687',
    customBtnBackgroundColor: 'var(--ff-teal-600)',
    h2Color: `#5f1212`,
    hoverColor: {
      h2: `#fff`,
      text: 'var(--ff-teal-600)',
      overlay: `var(--ff-teal-400)`,
      cardDescription: '#fff'
    },
    bullets: [
      'Instant access to all videos and bonuses',
      'Full Bouquet Maker brush set (150+ brushes)',
      'Bonus applications module (3 projects)',
      'Private Facebook community',
      'Extended License of the brush set ($40 value)',
      'Seamless patterns with transparent backgrounds process',
      'Rose simplification tutorial',
      'Composition design tutorials',
      '14 day money back guarantee',
      'Flat style cheat sheets (pdf)',
      'All project + demo color palettes',
      'English subtitles on all videos',
      'Basic + complex style practice exercises (12 total)',
    ],
  },
}
export const gqlFragmentProcreateStationeryDesignWMCD_Images = graphql`
    fragment FragmentProcreateStationeryDesignWMCD_Images on Query {
        WMCD1:imgixImage(url: "stationery-design-in-procreate/diff/diff-1.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD2:imgixImage(url: "stationery-design-in-procreate/diff/diff-2.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD3:imgixImage(url: "stationery-design-in-procreate/diff/diff-3.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD4:imgixImage(url: "stationery-design-in-procreate/diff/diff-4.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD5:imgixImage(url: "stationery-design-in-procreate/diff/diff-5.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD6:imgixImage(url: "stationery-design-in-procreate/diff/diff-6.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD7:imgixImage(url: "stationery-design-in-procreate/diff/diff-7.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD8:imgixImage(url: "stationery-design-in-procreate/diff/diff-8.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD9:imgixImage(url: "stationery-design-in-procreate/diff/diff-9.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD10:imgixImage(url: "stationery-design-in-procreate/diff/diff-10.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD11:imgixImage(url: "stationery-design-in-procreate/diff/diff-11.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD12:imgixImage(url: "stationery-design-in-procreate/diff/diff-12.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }

    }
`
export const gqlFragmentProcreateStationeryDesignWYL = graphql`
    fragment FragmentProcreateStationeryDesignWYL on Query {
        wylGridImage1:imgixImage(url: "stationery-design-in-procreate/wyl/wyl-1-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage2:imgixImage(url: "stationery-design-in-procreate/wyl/wyl-2-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage3:imgixImage(url: "stationery-design-in-procreate/wyl/wyl-3-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage4:imgixImage(url: "stationery-design-in-procreate/wyl/wyl-4-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage5:imgixImage(url: "stationery-design-in-procreate/wyl/wyl-6-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage6:imgixImage(url: "stationery-design-in-procreate/wyl/wyl-5-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage7:imgixImage(url: "stationery-design-in-procreate/wyl/wyl-7-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage8:imgixImage(url: "stationery-design-in-procreate/wyl/wyl-8-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage9:imgixImage(url: "stationery-design-in-procreate/wyl/wyl-9-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage10:imgixImage(url: "stationery-design-in-procreate/wyl/wyl-10-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage11:imgixImage(url: "stationery-design-in-procreate/wyl/wyl-11-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage12:imgixImage(url: "stationery-design-in-procreate/wyl/wyl-12-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylSquareImage1:imgixImage(url: "stationery-design-in-procreate/wyl/wyl-square-a.jpg") {
            gatsbyImageData(width:600, layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:600,
              auto: "compress"
          }, srcSetMaxWidth: 600)
        }
        wylSquareImage2:imgixImage(url: "stationery-design-in-procreate/wyl/wyl-square-b.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 800
            })
        }
    }
`
export const gqlFragmentProcreateStationeryDesignBrushTips = graphql`
    fragment FragmentProcreateStationeryDesignBrushTips on Query {
        brushTip_AE_1:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/01-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_2:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/02-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_3:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/03-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_4:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/04-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_5:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/05-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_6:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/06-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_7:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/07-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_8:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/08-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_9:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/09-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_10:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/10-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_11:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/11-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_12:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/12-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_16:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/16-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_13:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/13-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_14:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/14-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_AE_15:imgixImage(url: "stationery-design-in-procreate/brushes/artist-essentials/15-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_LS_1:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/landscape/01-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_LS_2:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/landscape/02-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_LS_3:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/landscape/03-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_LS_4:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/landscape/04-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_LS_5:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/landscape/05-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_LS_6:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/landscape/06-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_LS_7:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/landscape/07-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_LS_8:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/landscape/08-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_LS_9:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/landscape/09-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_LS_10:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/landscape/10-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_PR_1:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/portrait/01-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_PR_2:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/portrait/02-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_PR_3:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/portrait/03-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_PR_4:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/portrait/04-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_PR_5:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/portrait/05-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_PR_6:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/portrait/06-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_PR_7:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/portrait/07-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_PR_8:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/portrait/08-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_PR_9:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/portrait/09-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_PR_10:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/portrait/10-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_PR_11:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/portrait/11-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_PR_12:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/portrait/12-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_SQ_1:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/square/01-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_SQ_2:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/square/02-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_SQ_3:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/square/03-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_SQ_4:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/square/04-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_SQ_5:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/square/05-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_SQ_6:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/square/06-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_SQ_7:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/square/07-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_SQ_8:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/square/08-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_SQ_9:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/square/09-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_CS_SQ_10:imgixImage(url: "stationery-design-in-procreate/brushes/composition-stamps/square/10-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_1:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/01-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_2:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/02-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_3:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/03-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_4:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/04-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_5:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/05-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_6:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/06-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_7:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/07-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_8:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/08-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_9:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/09-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_10:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/10-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_11:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/11-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_12:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/12-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_13:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/13-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_14:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/14-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_15:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/15-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_16:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/16-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_17:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/17-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_18:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/18-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_19:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/19-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_20:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/20-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_21:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/21-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_22:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/22-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_23:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/23-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_24:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/24-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_25:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/25-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_HS_26:imgixImage(url: "stationery-design-in-procreate/brushes/holding-shapes/26-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_1: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_2: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_3: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-3.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_4: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-4.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_5: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-5.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_6: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-6.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_7: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-7.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_8: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-8.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_9: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-9.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_10: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-10.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_11: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-11.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_12: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-12.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_13: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-13.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_14: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-14.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_15: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-15.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_16: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-16.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_canvas_17: imgixImage(url: "stationery-design-in-procreate/brushes/canvas-templates/canvas-template-17.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
    }
`

export default course